import { cleanMetadataText } from "@/helpers/cleanMetadataText";
import { localeToRegionalTag } from "@/lib/i18n/siteLocale";
import type { Locale } from "./i18n/locales";
import { defaultLocale } from "./i18n/locales";
import { getMessages } from "./i18n/messages";

import { LocationEnum } from "./locationEnum";
import { locationStrings } from "./strings/locationStrings";

/** Revalidate static locale routes once per day (scheduled blog publish dates). */
export const DAILY_REVALIDATE_SECONDS = 24 * 60 * 60;

const phoneNumbers = {
  landau: {
    moritz: locationStrings[LocationEnum.Landau].phone.moritz,
    hubi: locationStrings[LocationEnum.Landau].phone.hubi,
  },
  koblenz: {
    moritz: locationStrings[LocationEnum.Koblenz].phone.moritz,
    hubi: locationStrings[LocationEnum.Koblenz].phone.hubi,
  },
  stuttgart: {
    moritz: locationStrings[LocationEnum.FrankfurtAmMain].phone.moritz,
    hubi: locationStrings[LocationEnum.FrankfurtAmMain].phone.hubi,
  },
};

export const phone = {
  general: phoneNumbers.stuttgart.hubi,
  moritz: phoneNumbers.landau.moritz,
  hubi: phoneNumbers.stuttgart.hubi,
};

// Location-specific phone numbers (fallback to general if not defined)
export const locationPhones: Partial<Record<LocationEnum, string>> = {
  [LocationEnum.Stuttgart]: phoneNumbers.stuttgart.hubi,
  [LocationEnum.Landau]: phoneNumbers.landau.moritz,
  [LocationEnum.Koblenz]: phoneNumbers.koblenz.moritz,
  [LocationEnum.München]: phoneNumbers.stuttgart.hubi,
  [LocationEnum.Kaiserslautern]: phoneNumbers.landau.hubi,
  [LocationEnum.Köln]: phoneNumbers.koblenz.hubi,
  [LocationEnum.Pirmasens]: phoneNumbers.landau.moritz,
  [LocationEnum.Heilbronn]: phoneNumbers.stuttgart.hubi,
};

export const getPhoneForLocation = (location?: LocationEnum): string => {
  if (location && locationPhones[location]) {
    return locationPhones[location]!;
  }
  return phone.general;
};

export const email = {
  general: "kontakt@solving.legal",
  solvingTax: "kontakt@solving.tax",
};

export const typeformIds = {
  contact: "yPt39pTi",
};

export const brevoConversationsId = "68a2fcaa78c633cec20da1c0";
export const truendoSiteId = "c95cd8ec-2bdf-4d44-8d5d-7efb1be78679";

export const slTrustpilotScore = 5;

export const heightNavigatonPx = 115;

/** Scroll margin for elements with in-page `id` so `scrollIntoView` / hash links clear the sticky nav. */
export const sectionAnchorScrollMarginPx = 140;

export const phoneNumber = phone.general;
export const emailAddressGeneral = email.general;
export const sloganResolvio = "Decide better with Resolvio";
export const domainResolvio = "https://resolvio.com";
export const domainSolvingLegal = "https://www.solving.legal";

export const nonbreakingSpace = "\u00A0";

export const currentYear = new Date().getFullYear();

export const responsiveGridSpacing = { xs: 2, sm: 2, md: 4, lg: 4 };

export const responsivePadding = {
  xs: responsiveGridSpacing.xs / 2,
  sm: responsiveGridSpacing.sm / 2,
  md: responsiveGridSpacing.md / 2,
  lg: responsiveGridSpacing.lg / 2,
};

export const numbers = {
  ustid: "DE459286506",
  hrb: "HRB 802505",
};

export const isLandauLive = true;
export const isGmbhLive = true;

export enum Firma {
  SolvingLegal = "SolvingLegal",
  SolvingTax = "SolvingTax",
}

export function getFirmaDisplayName(locale: Locale, id: Firma): string {
  const b = getMessages(locale).common.brand;
  if (id === Firma.SolvingLegal) {
    return isLandauLive && isGmbhLive
      ? b.firmaSolvingLegalGmbh
      : b.firmaSolvingLegalPreGmbh;
  }
  return b.firmaSolvingTax;
}

function homeJoinedDescription(locale: Locale): string {
  const h = getMessages(locale).home.header;
  return cleanMetadataText([h.title, h.subtitle, h.callToAction].join(" "));
}

function buildDefaultSiteMetadata(locale: Locale) {
  const m = getMessages(locale).common.metadata;
  const legalName = getFirmaDisplayName(locale, Firma.SolvingLegal);
  return {
    title: legalName,
    description: homeJoinedDescription(locale),
    metadataBase: new URL(domainSolvingLegal),
    keywords: m.seoKeywords,
    authors: [{ name: legalName }],
    robots: {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        "max-video-preview": -1,
        "max-image-preview": "large" as const,
        "max-snippet": -1,
      },
    },
    openGraph: {
      type: "website" as const,
      locale: localeToRegionalTag(locale),
      url: domainSolvingLegal,
      siteName: m.openGraphSiteName,
      title: m.openGraphTitle,
      description: m.openGraphDescription,
      images: [
        {
          url: "/images/og-image.png",
          width: 1200,
          height: 630,
          alt: m.openGraphImageAlt,
        },
      ],
    },
    twitter: {
      card: "summary_large_image" as const,
      title: m.twitterTitle,
      description: m.twitterDescription,
      images: ["/images/twitter-image.png"],
    },
    alternates: {
      canonical: domainSolvingLegal,
    },
  };
}

interface SlMetadataOptions {
  title?: string;
  description?: string;
  keywords?: string[];
  path?: string;
  /** Custom OG image URL (can be relative like /images/blog/my-image.jpg or absolute) */
  image?: string;
  /** Alt text for the OG image */
  imageAlt?: string;
  /** Page locale (Open Graph `locale` and default canonical locale context). */
  locale?: Locale;
  /** Next.js `alternates.languages` (hreflang → absolute URL). Omit when unknown. */
  languageAlternates?: Record<string, string>;
}

/**
 * Common title suffix for all pages
 */
export const titleSuffix = "Solving Legal";

/**
 * Generates a formatted title for expertise pages
 * @param expertiseTitle - The cleaned expertise title (e.g., "Arbeitsrecht")
 */
export const getExpertisePageTitle = (
  expertiseTitle: string,
  locale: Locale = defaultLocale,
): string => {
  const t = getMessages(locale).common.metadata.expertiseHubTitle;
  return t.replace("{expertise}", expertiseTitle);
};

/**
 * Generates a formatted title for city-specific expertise pages
 * @param expertiseTitle - The cleaned expertise title (e.g., "Arbeitsrecht")
 * @param cityName - The city name (e.g., "Kaiserslautern")
 */
export const getExpertiseCityPageTitle = (
  expertiseTitle: string,
  cityName: string,
  locale: Locale = defaultLocale,
): string => {
  const t = getMessages(locale).common.metadata.expertiseCityTitle;
  return t.replace("{expertise}", expertiseTitle).replace("{city}", cityName);
};

export const addTitleSuffix = (title: string): string => {
  return `${title} ${titleSuffix}`;
};

export const getSlMetadata = (options?: string | SlMetadataOptions | null) => {
  try {
    const baseTitle = cleanMetadataText(titleSuffix);

    // Handle legacy string parameter
    if (typeof options === "string") {
      const fullTitle = options ? `${options} - ${baseTitle}` : baseTitle;
      const base = buildDefaultSiteMetadata(defaultLocale);
      const ogLoc = localeToRegionalTag(defaultLocale);
      return {
        ...base,
        title: cleanMetadataText(fullTitle),
        openGraph: {
          ...base.openGraph,
          locale: ogLoc,
        },
      };
    }

    // Handle new object parameter
    if (options && typeof options === "object") {
      const fullTitle = options.title
        ? `${options.title} - ${baseTitle}`
        : baseTitle;
      const fullUrl = options.path
        ? `https://www.solving.legal${options.path}`
        : "https://www.solving.legal";
      const pageLocale = options.locale ?? defaultLocale;
      const base = buildDefaultSiteMetadata(pageLocale);
      const ogLoc = localeToRegionalTag(pageLocale);
      const langAlts = options.languageAlternates;
      const hasLangAlts = langAlts != null && Object.keys(langAlts).length > 0;

      // Build OG image - use custom image if provided, otherwise use default
      const m = getMessages(pageLocale).common.metadata;
      const ogImages = options.image
        ? [
            {
              url: options.image.startsWith("http")
                ? options.image
                : `${domainSolvingLegal}${options.image}`,
              width: 1200,
              height: 630,
              alt: options.imageAlt || options.title || m.openGraphImageAlt,
            },
          ]
        : base.openGraph.images;

      // Build Twitter image
      const twitterImages = options.image
        ? [
            options.image.startsWith("http")
              ? options.image
              : `${domainSolvingLegal}${options.image}`,
          ]
        : base.twitter.images;

      return {
        ...base,
        title: cleanMetadataText(fullTitle),
        description: options.description || base.description,
        keywords: options.keywords
          ? options.keywords.join(", ")
          : base.keywords,
        openGraph: {
          ...base.openGraph,
          title: cleanMetadataText(fullTitle),
          description: options.description || base.openGraph.description,
          url: fullUrl,
          locale: ogLoc,
          images: ogImages,
        },
        twitter: {
          ...base.twitter,
          title: cleanMetadataText(fullTitle),
          description: options.description || base.twitter.description,
          images: twitterImages,
        },
        alternates: {
          canonical: fullUrl,
          ...(hasLangAlts ? { languages: langAlts } : {}),
        },
      };
    }

    // Default case
    const base = buildDefaultSiteMetadata(defaultLocale);
    return {
      ...base,
      title: baseTitle,
      openGraph: {
        ...base.openGraph,
        locale: localeToRegionalTag(defaultLocale),
      },
    };
  } catch (error) {
    console.error("Error generating metadata:", error);
    const fb = getMessages(defaultLocale).common.metadata;
    return {
      title: fb.fallbackTitle,
      description: fb.fallbackDescription,
      metadataBase: new URL("https://www.solving.legal"),
    };
  }
};
